/**
 * NotesWindow — Full-screen modal overlay for the tabbed research notebook.
 *
 * Layout:
 *   ┌──────────────────────────────────────────────────────┐
 *   │  HEADER: Company Name  ·  [Ctrl+N close]   [✕]       │
 *   ├──────────────────────────────────────────────────────┤
 *   │  TAB BAR: [Thesis] [Risks] [Competitors] [...] [+]   │
 *   ├──────────────────────────────────────────────────────┤
 *   │                                                      │
 *   │                  TAB CONTENT                          │
 *   │              (rich text editor)                       │
 *   │                                                      │
 *   ├──────────────────────────────────────────────────────┤
 *   │  FOOTER: Save status · Word count                    │
 *   └──────────────────────────────────────────────────────┘
 */
import React, { useEffect, useCallback } from 'react';
import { IconButton, Tooltip } from '@mui/material';
import { Close, MenuBook } from '@mui/icons-material';
import TabBar from './TabBar';
import TabContent from './TabContent';
import '../../styles/notesWindow.css';

export default function NotesWindow({
    isOpen,
    onClose,
    companyId,
    companyName,
    tabs,
    activeTabId,
    setActiveTabId,
    activeTab,
    loading,
    fetchNotes,
    addTab,
    removeTab,
    renameTab,
    updateLocalTabContent,
    saveStatus,
    errorMsg,
    scheduleSave,
    flushSave,
    manualSave,
}) {
    /* ── Fetch notes when opened ── */
    useEffect(() => {
        if (isOpen && companyId) {
            console.log('[NotesWindow] Modal opened, fetching notes for:', companyId);
            fetchNotes(true); // Force fetch to get latest data
        }
    }, [isOpen, companyId, fetchNotes]);

    /* ── Keyboard: Escape to close, Ctrl+S to save ── */
    useEffect(() => {
        if (!isOpen) return;
        const handleKey = (e) => {
            // Always stop propagation so parent handlers don't fire
            e.stopPropagation();
            if (e.key === 'Escape') {
                e.preventDefault();
                flushSave?.();
                onClose();
            }
            if ((e.ctrlKey || e.metaKey) && e.key === 's') {
                e.preventDefault();
                flushSave?.();
            }
        };
        window.addEventListener('keydown', handleKey, true); // capture phase
        return () => window.removeEventListener('keydown', handleKey, true);
    }, [isOpen, onClose, flushSave]);

    /* ── Content change handler ── */
    const handleContentChange = useCallback(({ richContent, plainText }) => {
        if (!activeTabId) return;
        // Update local state immediately
        updateLocalTabContent(activeTabId, { richContent, plainText });
        // Schedule auto-save
        scheduleSave(activeTabId, {
            title: activeTab?.tabName || '',
            richContent,
            plainText,
        });
    }, [activeTabId, activeTab, updateLocalTabContent, scheduleSave]);

    /* ── Close handler (flush pending saves) ── */
    const handleClose = useCallback((e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        flushSave?.();
        onClose();
    }, [flushSave, onClose]);

    /* ── Overlay click (only close when clicking the backdrop itself) ── */
    const handleOverlayClick = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        // Only close if the click is directly on the overlay, not a child
        if (e.target === e.currentTarget) {
            handleClose();
        }
    }, [handleClose]);

    if (!isOpen) return null;

    return (
        <div className="nw-overlay" onClick={handleOverlayClick} onMouseDown={(e) => e.stopPropagation()}>
            <div className="nw-window" onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
                {/* ── Header ── */}
                <div className="nw-header">
                    <div className="nw-header-left">
                        <MenuBook sx={{ fontSize: 20, color: 'var(--brand)' }} />
                        <span className="nw-header-title">Research Notes</span>
                        <span className="nw-header-company">{companyName || companyId}</span>
                    </div>
                    <div className="nw-header-right">
                        <span className="nw-header-shortcut">Esc to close</span>
                        <Tooltip title="Close" arrow>
                            <IconButton size="small" onClick={handleClose} type="button" sx={{ color: 'var(--text-2)' }}>
                                <Close sx={{ fontSize: 18 }} />
                            </IconButton>
                        </Tooltip>
                    </div>
                </div>

                {/* ── Tab bar ── */}
                <TabBar
                    tabs={tabs}
                    activeTabId={activeTabId}
                    onSelectTab={setActiveTabId}
                    onAddTab={addTab}
                    onDeleteTab={removeTab}
                    onRenameTab={renameTab}
                />

                {/* ── Content area ── */}
                {loading ? (
                    <div className="nw-loading">
                        <div className="nw-loading-spinner" />
                        <span>Loading notes…</span>
                    </div>
                ) : (
                    <TabContent
                        tab={activeTab}
                        onContentChange={handleContentChange}
                        saveStatus={saveStatus}
                        errorMsg={errorMsg}
                        companyId={companyId}
                        onManualSave={() => manualSave?.(activeTabId, {
                            title: activeTab?.tabName || '',
                            richContent: activeTab?.content?.richContent || '',
                            plainText: activeTab?.content?.plainText || '',
                        })}
                    />
                )}
            </div>
        </div>
    );
}
